/**
 * @desc        Root app js
 * @version     1.3 (Removed depracted theme provider for MUI V4) - 29 October 2024
 * */

import { Capacitor } from '@capacitor/core'
import { SplashScreen } from '@capacitor/splash-screen'
import { AWSCOGNITO_CLIENTID, AWSCOGNITO_USERPOOLID, is_mobile_build } from 'app-config/index.js'
import Amplify from 'aws-amplify'
import gsap from 'gsap'
import { AnimatedLoader } from 'helpers/loaders.js'
import 'normalize.css'
import React, { Suspense, useEffect } from 'react'
import fastCompare from 'react-fast-compare'
import { useSelector } from 'react-redux'
import { useResizeDetector } from 'react-resize-detector/build/withPolyfill'
import { ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import { DBProvider } from 'redux/local-db/dexie-db'
import Routes from 'routes'
import './App.scss'
import './international/i18n.js'

const awsConfig = {
    Auth: {
        region: 'ap-southeast-2',
        userPoolId: AWSCOGNITO_USERPOOLID,
        userPoolWebClientId: AWSCOGNITO_CLIENTID,
    },
}

Amplify.configure(awsConfig)

gsap.registerEffect({
    name: 'pulse',
    effect(targets, vars) {
        return gsap.fromTo(
            targets,
            {
                opacity: 0.01,
                y: '+=24',
            },
            {
                delay: vars,
                duration: 0.25,
                ease: 'power2.inOut',
                opacity: 1,
                y: 0,
            },
        )
    },
})

const KEYBOARD_INSET_THRESHOLD = 120
const KEYBOARD_SCROLL_DELAY_MS = 325
const KEYBOARD_UPDATE_DELAY_MS = 250

const isEditableElement = element => {
    if (!(element instanceof HTMLElement)) {
        return false
    }

    if (element.isContentEditable) {
        return true
    }

    return ['INPUT', 'SELECT', 'TEXTAREA'].includes(element.tagName)
}

const App = props => {
    const { colorScheme, enableFixedHeader, enableFixedSidebar, enableFixedFooter, enableClosedSidebar, closedSmallerSidebar, enableMobileMenu, enablePageTabsAlt, highContrast } = useSelector(state => state.theme, fastCompare)
    const minWidth = 1250

    const { width, ref } = useResizeDetector()

    const isNarrowViewport = is_mobile_build ? (width ?? (typeof window !== 'undefined' ? window.innerWidth : minWidth)) < minWidth : width < minWidth

    useEffect(() => {
        if (Capacitor.getPlatform() !== 'web') {
            SplashScreen.hide()
        }
    }, [])

    useEffect(() => {
        if (Capacitor.getPlatform() === 'web') {
            return undefined
        }

        const rootElement = document.documentElement
        const viewport = window.visualViewport
        let frameId = null
        let scrollTimerId = null
        let updateTimerId = null

        const clearScheduledWork = () => {
            if (frameId !== null) {
                window.cancelAnimationFrame(frameId)
                frameId = null
            }

            if (scrollTimerId !== null) {
                window.clearTimeout(scrollTimerId)
                scrollTimerId = null
            }

            if (updateTimerId !== null) {
                window.clearTimeout(updateTimerId)
                updateTimerId = null
            }
        }

        const getKeyboardInset = () => {
            if (!viewport) {
                return 0
            }

            const keyboardInset = Math.round(Math.max(window.innerHeight - viewport.height - viewport.offsetTop, 0))

            return keyboardInset > KEYBOARD_INSET_THRESHOLD ? keyboardInset : 0
        }

        const applyKeyboardInset = () => {
            const keyboardInset = getKeyboardInset()

            rootElement.classList.toggle('fd-keyboard-open', keyboardInset > 0)
            rootElement.style.setProperty('--fd-native-keyboard-inset', `${keyboardInset}px`)
        }

        const scheduleKeyboardInsetUpdate = delay => {
            if (updateTimerId !== null) {
                window.clearTimeout(updateTimerId)
                updateTimerId = null
            }

            if (frameId !== null) {
                window.cancelAnimationFrame(frameId)
                frameId = null
            }

            const runUpdate = () => {
                frameId = window.requestAnimationFrame(() => {
                    frameId = null
                    applyKeyboardInset()
                })
            }

            if (delay > 0) {
                updateTimerId = window.setTimeout(() => {
                    updateTimerId = null
                    runUpdate()
                }, delay)
                return
            }

            runUpdate()
        }

        const scheduleFocusedElementScroll = () => {
            if (scrollTimerId !== null) {
                window.clearTimeout(scrollTimerId)
            }

            scrollTimerId = window.setTimeout(() => {
                scrollTimerId = null

                const activeElement = document.activeElement

                if (!isEditableElement(activeElement)) {
                    return
                }

                activeElement.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                    inline: 'nearest',
                })
            }, KEYBOARD_SCROLL_DELAY_MS)
        }

        const handleFocusIn = event => {
            if (!isEditableElement(event.target)) {
                return
            }

            scheduleKeyboardInsetUpdate(KEYBOARD_UPDATE_DELAY_MS)
            scheduleFocusedElementScroll()
        }

        const handleFocusOut = () => {
            scheduleKeyboardInsetUpdate(KEYBOARD_UPDATE_DELAY_MS)
        }

        const handleViewportChange = () => {
            scheduleKeyboardInsetUpdate(0)
        }

        if (viewport) {
            viewport.addEventListener('resize', handleViewportChange)
            viewport.addEventListener('scroll', handleViewportChange)
        }

        window.addEventListener('focusin', handleFocusIn)
        window.addEventListener('focusout', handleFocusOut)

        scheduleKeyboardInsetUpdate(0)

        return () => {
            clearScheduledWork()

            if (viewport) {
                viewport.removeEventListener('resize', handleViewportChange)
                viewport.removeEventListener('scroll', handleViewportChange)
            }

            window.removeEventListener('focusin', handleFocusIn)
            window.removeEventListener('focusout', handleFocusOut)
            rootElement.classList.remove('fd-keyboard-open')
            rootElement.style.removeProperty('--fd-native-keyboard-inset')
        }
    }, [])

    const containerClass = `farmdeck app-container app-theme-${colorScheme} 
    ${enableFixedHeader ? 'fixed-header' : ''} 
    ${enableFixedSidebar || isNarrowViewport ? 'fixed-sidebar' : ''} 
    ${enableFixedFooter ? 'fixed-footer' : ''} 
    ${enableClosedSidebar || isNarrowViewport ? 'closed-sidebar' : ''} 
    ${closedSmallerSidebar || isNarrowViewport ? 'closed-sidebar-mobile' : ''} 
    ${enableMobileMenu ? 'sidebar-mobile-open' : ''} 
    ${enablePageTabsAlt ? 'body-tabs-shadow-btn' : ''} 
    ${process.env.REACT_APP_BUILD_MODE !== 'web' ? 'native-container' : ''} 
    ${highContrast}`

    return (
        <DBProvider>
            <div ref={ref} className={containerClass}>
                <Suspense fallback={AnimatedLoader}>
                    <Routes />
                </Suspense>
                <ToastContainer autoClose={5000} className="toasty" id="toast-container" pauseOnFocusLoss={false} position="top-center" stacked theme="dark" />
            </div>
        </DBProvider>
    )
}

export default App
